import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [isAuto, setIsAuto] = useState(true);

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('codefussion-theme');
    const savedAuto = localStorage.getItem('codefussion-theme-auto');
    
    if (savedTheme && savedAuto === 'false') {
      setTheme(savedTheme);
      setIsAuto(false);
    } else {
      // Auto mode - detect system preference
      detectSystemTheme();
    }
  }, []);

  // Detect system theme changes
  useEffect(() => {
    if (!isAuto) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      setTheme(e.matches ? 'dark' : 'light');
    };

    // Set initial theme
    handleChange(mediaQuery);
    
    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isAuto]);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.className = theme;
  }, [theme]);

  const detectSystemTheme = () => {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setTheme(isDark ? 'dark' : 'light');
    setIsAuto(true);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    setIsAuto(false);
    localStorage.setItem('codefussion-theme', newTheme);
    localStorage.setItem('codefussion-theme-auto', 'false');
  };

  const setAutoTheme = () => {
    setIsAuto(true);
    localStorage.setItem('codefussion-theme-auto', 'true');
    detectSystemTheme();
  };

  const value = {
    theme,
    isAuto,
    toggleTheme,
    setAutoTheme,
    setTheme: (newTheme) => {
      setTheme(newTheme);
      setIsAuto(false);
      localStorage.setItem('codefussion-theme', newTheme);
      localStorage.setItem('codefussion-theme-auto', 'false');
    }
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};